// ./src/pages/private/EventDiscountCodesPage/DiscountCodeDrawer.tsx
//
// Add / edit one checkout discount code. A code takes a percentage off OR a fixed
// amount off (the segmented control chooses which), with optional limits (min
// order, total uses, per-customer uses), an optional active window, and what it
// applies to. The code itself is uppercased so matching is case-insensitive.
// Uses the shared AppDrawer + form fields.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Stack, Group, Text, Divider } from '@mantine/core';
import { useTranslation } from 'react-i18next';

import { OpResult } from '@sdflc/api-helpers';

import { DiscountCode, DiscountCodeInput, DiscountCodeService } from '@/utils/api';
import { useToast, useModalShortcuts, useFormSnapshot, SHORTCUT_HINTS } from '@/hooks';
import { getFormErrors, formatErrors, moveEndWithStart, utcToZonedString, zonedStringToUtc } from '@/utils';
import {
  AppDrawer,
  TextField,
  TextAreaField,
  SelectField,
  NumberField,
  SegmentedField,
  DateTimeField,
} from '@/components/generic';
import { eventZoneNote } from '@/components/features/Events';
import { CURRENCY_OPTIONS, getCurrencyShortLabel } from '@/config/currencyOptions';

import { DiscountTicketTypesField } from './DiscountTicketTypesField';

const PERCENT = 'percent';
const AMOUNT = 'amount';
type DiscountKind = typeof PERCENT | typeof AMOUNT;

const APPLIES_TO = {
  TICKETS: 'tickets',
  ALL: 'all',
  // #163 — only the picked ticket types (the server's DISCOUNT_APPLIES_TO.TICKET_TYPES).
  TICKET_TYPES: 'ticket_types',
} as const;

const CURRENCY_DATA = CURRENCY_OPTIONS.map((c) => ({ value: c.value, label: getCurrencyShortLabel(c) }));


// Server error names that map to a visible input: known → inline under the field,
// anything else → generic, in the alert above Save.
const KNOWN_FORM_FIELDS = ['code', 'name', 'description', 'discountKind', 'percentOff', 'amountOff', 'currencyCode', 'minOrderAmount', 'maxUses', 'maxUsesPerCustomer', 'startsAt', 'endsAt', 'appliesTo', 'ticketTypeIds'];

interface CodeForm {
  code: string;
  name: string;
  description: string;
  discountKind: DiscountKind;
  percentOff: number | null;
  amountOff: number | null;
  currencyCode: string;
  minOrderAmount: number | null;
  maxUses: number | null;
  maxUsesPerCustomer: number | null;
  startsAt: string | null;
  endsAt: string | null;
  appliesTo: string;
  ticketTypeIds: string[];
}

const emptyForm = (defaultCurrency: string): CodeForm => ({
  code: '',
  name: '',
  description: '',
  discountKind: PERCENT,
  percentOff: null,
  amountOff: null,
  currencyCode: defaultCurrency,
  minOrderAmount: null,
  maxUses: null,
  maxUsesPerCustomer: null,
  startsAt: null,
  endsAt: null,
  appliesTo: APPLIES_TO.TICKETS,
  ticketTypeIds: [],
});

// ⚠ The active window is a wall clock in the EVENT's zone (ROADMAP #93) — loaded and saved there.
const toForm = (c: DiscountCode, defaultCurrency: string, tz: string | null): CodeForm => {
  // A fixed amount takes precedence when both happen to be set.
  const discountKind: DiscountKind = c.amountOff != null ? AMOUNT : PERCENT;
  return {
    code: c.code ?? '',
    name: c.name ?? '',
    description: c.description ?? '',
    discountKind,
    percentOff: c.percentOff ?? null,
    amountOff: c.amountOff ?? null,
    currencyCode: c.currencyCode ?? defaultCurrency,
    minOrderAmount: c.minOrderAmount ?? null,
    maxUses: c.maxUses ?? null,
    maxUsesPerCustomer: c.maxUsesPerCustomer ?? null,
    startsAt: utcToZonedString(c.startsAt, tz, false),
    endsAt: utcToZonedString(c.endsAt, tz, false),
    appliesTo: c.appliesTo ?? APPLIES_TO.TICKETS,
    ticketTypeIds: Array.isArray(c.ticketTypeIds) ? c.ticketTypeIds : [],
  };
};

export interface DiscountCodeDrawerProps {
  opened: boolean;
  onClose: () => void;
  eventId: string;
  /** Event's currency, used as the default for fixed-amount codes. */
  eventCurrency?: string | null;
  code: DiscountCode | null;
  onSaved: () => void;
  /** The event's time zone — the active window is entered and saved in it (ROADMAP #93). ⚠ Required. */
  timezone: string | null;
}

export const DiscountCodeDrawer: React.FC<DiscountCodeDrawerProps> = ({
  opened,
  onClose,
  eventId,
  eventCurrency,
  code,
  onSaved,
  timezone,
}) => {
  const { t } = useTranslation(['events', 'common']);
  const toast = useToast();
  // The active window is in the event's zone — said when this device keeps another one.
  const zoneNote = eventZoneNote(t, timezone, 'elsewhere');
  const defaultCurrency = eventCurrency || 'USD';
  const [form, setForm] = useState<CodeForm>(() => emptyForm(defaultCurrency));
  const [saving, setSaving] = useState(false);
  const [submitError, setSubmitError] = useState<OpResult | null>(null);

  const { fields: fieldErrors, genericErrors } = useMemo(
    () => getFormErrors(submitError, KNOWN_FORM_FIELDS),
    [submitError],
  );
  const codeRef = useRef<HTMLInputElement>(null);

  // Esc / a click outside / Cancel ask before discarding a changed code (#123).
  const { isDirty, reseed, clear } = useFormSnapshot(form);

  useEffect(() => {
    if (!opened) {
      clear();

      return;
    }

    // A refreshed code keeps what the user has typed (#125); another code replaces the form.
    const seeded = code ? toForm(code, defaultCurrency, timezone) : emptyForm(defaultCurrency);
    setForm(reseed(seeded, code?.id ?? "__new__"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, code]);

  useEffect(() => {
    if (!opened) return undefined;
    const id = setTimeout(() => {
      const el = codeRef.current;
      if (el) {
        el.focus();
        const len = el.value.length;
        el.setSelectionRange(len, len);
      }
    }, 120);
    return () => clearTimeout(id);
  }, [opened]);

  const handleChange = useCallback((name: string | undefined, value: unknown) => {
    if (!name) return;
    setForm((f) => {
      // Moving the start moves the end by the same amount (the code keeps its validity length).
      if (name === 'startsAt') {
        const v = (value as string | null) ?? null;

        return { ...f, startsAt: v, endsAt: v ? moveEndWithStart(f.startsAt, f.endsAt, v) : f.endsAt };
      }

      return { ...f, [name]: value };
    });
  }, []);

  const isPercent = form.discountKind === PERCENT;

  // Guardrails: a code needs a positive discount, percentages cap at 100, and the
  // window (if both set) must be ordered.
  const discountValue = isPercent ? form.percentOff : form.amountOff;
  const windowOrdered = !form.startsAt || !form.endsAt || new Date(form.endsAt) >= new Date(form.startsAt);
  const forTypes = form.appliesTo === APPLIES_TO.TICKET_TYPES;
  const canSave =
    !!form.code.trim() &&
    (!forTypes || form.ticketTypeIds.length > 0) &&
    discountValue != null &&
    discountValue > 0 &&
    (!isPercent || discountValue <= 100) &&
    windowOrdered &&
    !saving;

  const QUESTION_TYPES = useMemo(
    () => [
      { value: PERCENT, label: t('events:discounts.kindPercent', { defaultValue: 'Percentage off' }) },
      { value: AMOUNT, label: t('events:discounts.kindAmount', { defaultValue: 'Fixed amount off' }) },
    ],
    [t],
  );

  const appliesToData = useMemo(
    () => [
      { value: APPLIES_TO.TICKETS, label: t('events:discounts.appliesTickets', { defaultValue: 'Tickets only' }) },
      { value: APPLIES_TO.ALL, label: t('events:discounts.appliesAll', { defaultValue: 'Whole order' }) },
      { value: APPLIES_TO.TICKET_TYPES, label: t('events:discounts.appliesTypes', { defaultValue: 'Specific ticket types' }) },
    ],
    [t],
  );

  const save = async () => {
    if (!canSave) return;
    setSaving(true);
    setSubmitError(null);

    const input: DiscountCodeInput = {
      code: form.code.trim().toUpperCase(),
      name: form.name.trim() || null,
      description: form.description.trim() || null,
      percentOff: isPercent ? form.percentOff : null,
      amountOff: isPercent ? null : form.amountOff,
      currencyCode: isPercent ? null : form.currencyCode || null,
      minOrderAmount: form.minOrderAmount,
      maxUses: form.maxUses,
      maxUsesPerCustomer: form.maxUsesPerCustomer,
      startsAt: zonedStringToUtc(form.startsAt, timezone),
      endsAt: zonedStringToUtc(form.endsAt, timezone),
      appliesTo: form.appliesTo,
      // Only a code limited to types keeps any (the server clears them for the other modes too).
      ticketTypeIds: forTypes ? form.ticketTypeIds : [],
    };

    const result = code?.id
      ? await DiscountCodeService.update(code.id, input)
      : await DiscountCodeService.create({ ...input, eventId });

    setSaving(false);
    if (!result.didSucceed()) {
      // Keep the OpResult: field errors go under their inputs, the rest above Save.
      setSubmitError(result);

      return;
    }
    toast.success(t('events:discounts.saved', { defaultValue: 'Discount code saved.' }));
    onSaved();
    onClose();
  };

  useModalShortcuts({ onConfirm: save, canConfirm: canSave, enabled: opened });

  const title = code
    ? t('events:discounts.editTitle', { defaultValue: 'Edit discount code' })
    : t('events:discounts.addTitle', { defaultValue: 'Add discount code' });

  return (
    <AppDrawer
      opened={opened}
      onClose={onClose}
      title={title}
      size="md"
      loading={saving}
      errors={formatErrors(genericErrors)}
      hasFieldErrors={Object.values(fieldErrors).some(Boolean)}
      actionType="save-cancel"
      onConfirm={save}
      confirmLabel={t('common:buttons.save', { defaultValue: 'Save' })}
      cancelLabel={t('common:buttons.cancel', { defaultValue: 'Cancel' })}
      confirmDisabled={!canSave}
      confirmShortcut={SHORTCUT_HINTS.save}
      isDirty={isDirty}
    >
      <Stack gap="md">
        <TextField
          ref={codeRef}
          name="code"
          label={t('events:discounts.code', { defaultValue: 'Code' })}
          description={t('events:discounts.codeHint', { defaultValue: 'What guests type at checkout — matched case-insensitively.' })}
          placeholder={t('events:discounts.codePh', { defaultValue: 'e.g. EARLYBIRD' })}
          value={form.code}
          onChange={(n, v) => handleChange(n, ((v as string) ?? '').toUpperCase())}
          required
          error={fieldErrors.code}
        />

        <TextField
          name="name"
          label={t('events:discounts.name', { defaultValue: 'Internal name' })}
          description={t('events:discounts.nameHint', { defaultValue: 'Only you see this — helps you tell codes apart.' })}
          placeholder={t('events:discounts.namePh', { defaultValue: 'e.g. Early bird promotion' })}
          value={form.name}
          onChange={handleChange}
          error={fieldErrors.name}
        />

        <div>
          <Text fw={600} size="md" mb="xs">
            {t('events:discounts.discount', { defaultValue: 'Discount' })}
          </Text>
          <SegmentedField
            name="discountKind"
            data={QUESTION_TYPES}
            value={form.discountKind}
            onChange={handleChange}
            fullWidth
            error={fieldErrors.discountKind}
          />
          <Group gap="sm" mt="sm" wrap="nowrap" align="flex-start">
            {isPercent ? (
              <NumberField
                name="percentOff"
                label={t('events:discounts.percentOff', { defaultValue: 'Percentage off' })}
                value={form.percentOff}
                onChange={handleChange}
                min={0}
                max={100}
                suffix="%"
                style={{ flex: 1 }}
                error={fieldErrors.percentOff}
              />
            ) : (
              <>
                <NumberField
                  name="amountOff"
                  label={t('events:discounts.amountOff', { defaultValue: 'Amount off' })}
                  value={form.amountOff}
                  onChange={handleChange}
                  min={0}
                  decimalScale={2}
                  style={{ flex: 1 }}
                  error={fieldErrors.amountOff}
                />
                <SelectField
                  name="currencyCode"
                  label={t('events:discounts.currency', { defaultValue: 'Currency' })}
                  data={CURRENCY_DATA}
                  value={form.currencyCode}
                  onChange={handleChange}
                  searchable
                  allowDeselect={false}
                  w={130}
                  error={fieldErrors.currencyCode}
                />
              </>
            )}
          </Group>
        </div>

        <Divider />

        <NumberField
          name="minOrderAmount"
          label={t('events:discounts.minOrder', { defaultValue: 'Minimum order to qualify' })}
          description={t('events:discounts.minOrderHint', { defaultValue: 'Leave empty for no minimum.' })}
          value={form.minOrderAmount}
          onChange={handleChange}
          min={0}
          decimalScale={2}
          placeholder={t('events:discounts.noMinimum', { defaultValue: 'No minimum' })}
          error={fieldErrors.minOrderAmount}
        />

        <Group gap="sm" grow align="flex-start">
          <NumberField
            name="maxUses"
            label={t('events:discounts.maxUses', { defaultValue: 'Total redemptions' })}
            value={form.maxUses}
            onChange={handleChange}
            min={1}
            allowDecimal={false}
            placeholder={t('events:discounts.unlimited', { defaultValue: 'Unlimited' })}
            error={fieldErrors.maxUses}
          />
          <NumberField
            name="maxUsesPerCustomer"
            label={t('events:discounts.maxPerCustomer', { defaultValue: 'Per customer' })}
            value={form.maxUsesPerCustomer}
            onChange={handleChange}
            min={1}
            allowDecimal={false}
            placeholder={t('events:discounts.unlimited', { defaultValue: 'Unlimited' })}
            error={fieldErrors.maxUsesPerCustomer}
          />
        </Group>

        <SelectField
          name="appliesTo"
          label={t('events:discounts.appliesTo', { defaultValue: 'Applies to' })}
          data={appliesToData}
          value={form.appliesTo}
          onChange={handleChange}
          allowDeselect={false}
          error={fieldErrors.appliesTo}
        />

        {forTypes && (
          <DiscountTicketTypesField
            eventId={eventId}
            value={form.ticketTypeIds}
            onChange={(ids) => handleChange('ticketTypeIds', ids)}
            disabled={saving}
            error={fieldErrors.ticketTypeIds}
          />
        )}

        <Divider />

        <Text fw={600} size="md">
          {t('events:discounts.activeWindow', { defaultValue: 'Active window' })}
        </Text>
        <Text size="sm" c="dimmed" mt={-8}>
          {t('events:discounts.activeWindowHint', { defaultValue: 'Optional — leave empty and the code works any time.' })}
        </Text>
        {zoneNote && (
          <Text size="md" c="dimmed" mt={-8}>
            {zoneNote}
          </Text>
        )}
        <Group gap="sm" grow align="flex-start">
          <DateTimeField
            name="startsAt"
            label={t('events:discounts.startsAt', { defaultValue: 'Starts' })}
            value={form.startsAt}
            onChange={handleChange}
            clearable
            error={fieldErrors.startsAt}
          />
          <DateTimeField
            name="endsAt"
            label={t('events:discounts.endsAt', { defaultValue: 'Ends' })}
            value={form.endsAt}
            onChange={handleChange}
            notBefore={form.startsAt}
            clearable
            error={
              fieldErrors.endsAt ||
              (!windowOrdered ? t('events:discounts.windowError', { defaultValue: 'End must be after the start.' }) : undefined)
            }
          />
        </Group>

        <TextAreaField
          name="description"
          label={t('events:discounts.description', { defaultValue: 'Description' })}
          description={t('events:discounts.descriptionHint', { defaultValue: 'Optional note about this code.' })}
          value={form.description}
          onChange={handleChange}
          autosize
          minRows={2}
          maxRows={4}
          error={fieldErrors.description}
        />
      </Stack>
    </AppDrawer>
  );
};
